import type React from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../../controls/icons';
import { useWordsHappenTheme, WordsHappenTheme } from '../../theme';
import { focusableWithin, trapTab } from './focusable';
import styles from './modal.module.scss';

export interface WordsHappenModalProps {
  open: boolean;
  onClose: () => void;
  /** Visible heading, and the dialog's accessible name. */
  title: React.ReactNode;
  /** Supporting line under the heading, and the dialog's accessible description. */
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const lockScroll = (): (() => void) => {
  const root = document.documentElement;
  const previous = root.style.overflow;
  root.style.overflow = 'hidden';
  return () => {
    root.style.overflow = previous;
  };
};

/**
 * A modal dialog: a bottom sheet on phones, a centred card on wider screens. Focus moves into it on open
 * and is trapped there, Escape or a backdrop click closes it, and focus returns to whatever opened it.
 * It renders in a portal re-wrapped in the current theme, so its tokens match the page.
 */
export const WordsHappenModal: React.FC<WordsHappenModalProps> = (props) =>
  props.open ? <OpenModal {...props} /> : null;

const OpenModal: React.FC<WordsHappenModalProps> = ({ onClose, title, description, children, className }) => {
  const { theme } = useWordsHappenTheme();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockScroll = lockScroll();

    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else dialog.setAttribute('open', '');

    // Land on the first choice in the body, falling back to the close button.
    (focusableWithin(bodyRef.current)[0] ?? focusableWithin(dialog)[0])?.focus();

    // The browser's own Escape handling; routed through onClose so the parent stays in control.
    const onCancel = (event: Event) => {
      event.preventDefault();
      onCloseRef.current();
    };
    dialog.addEventListener('cancel', onCancel);

    return () => {
      dialog.removeEventListener('cancel', onCancel);
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      unlockScroll();
      opener?.focus();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Tab') {
      trapTab(event, dialogRef.current);
    }
  };

  // A click on the ::backdrop lands on the dialog itself, outside its box.
  const onClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target !== dialogRef.current) return;
    const rect = dialogRef.current.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) onClose();
  };

  const cls = [styles.dialog, theme === 'meadow' ? styles.meadow : styles.flat, className].filter(Boolean).join(' ');

  return createPortal(
    <WordsHappenTheme theme={theme} className={styles.portal}>
      <dialog
        ref={dialogRef}
        className={cls}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        onKeyDown={onKeyDown}
        onClick={onClick}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            <CloseIcon size={20} strokeWidth={2} />
          </button>
        </header>
        {description && (
          <p id={descriptionId} className={styles.description}>
            {description}
          </p>
        )}
        <div ref={bodyRef} className={styles.body}>
          {children}
        </div>
      </dialog>
    </WordsHappenTheme>,
    document.body,
  );
};
